import { useState, useCallback, useMemo } from 'react';
import { binanceService, EnrichedCandleData } from '@/services/binanceService';
import { supabase } from '@/integrations/supabase/client';

// =====================================================================
// ========================== TIPOS E INTERFACES ========================
// =====================================================================

export interface AlphaQuantConfig {
  // Fontes
  symbol: string;
  
  // Thresholds 24H
  oi_up_lsq: number;        // OI_up ≥ % (L-SQ)
  oi_down_rec: number;      // OI_down ≤ % (REC)
  px_lo: number;            // Banda preço baixa %
  px_hi: number;            // Banda preço alta %
  fund_bp_lsq: number;      // Δ funding ≤ (bps) p/ L-SQ
  fund_bp_rec: number;      // Δ funding ≤ (bps) p/ REC
  px_hold_rec: number;      // Price hold ≥ % (REC)
  
  // Reversões (TOP-REV / BOT-REV)
  diff_pp: number;          // Limiar |Preço−OI| (p.p.) p/ triggers
  top_px_up_24: number;     // TOP: ΔPreço 24h ≥ %
  top_diff_neg_24: number;  // TOP: Diff 24h ≤ − (p.p.)
  top_fund_lvl_bps: number; // TOP: Funding 24h ≥ (bps)
  top_fund_delta_bps: number; // TOP: ΔFunding_N ≥ (bps)
  bot_px_dn_24: number;     // BOT: ΔPreço 24h ≤ − %
  bot_diff_pos_24: number;  // BOT: Diff 24h ≥ + (p.p.)
  bot_fund_lvl_bps: number; // BOT: Funding 24h ≤ − (bps)
  bot_fund_delta_bps: number; // BOT: ΔFunding_N ≤ − (bps)
  
  // Triggers TOP-REV
  use_trg_diff_top: boolean;
  use_trg_fund_top: boolean;
  use_trg_oi_top: boolean;
  
  // Triggers BOT-REV
  use_trg_diff_bot: boolean;
  use_trg_fund_bot: boolean;
  use_trg_oi_bot: boolean;
  bot_oi_flush_E: number;   // BOT: E (flush OI curto, %)
  
  // Sinais ativos
  enable_lsq: boolean;
  enable_rec: boolean;
  enable_top_rev: boolean;
  enable_bot_rev: boolean;
  
  // Stop/TP
  lsq_sl_pct: number;
  lsq_tp_pct: number;
  rec_sl_pct: number;
  rec_tp_pct: number;
  top_sl_pct: number;
  top_tp_pct: number;
  bot_sl_pct: number;
  bot_tp_pct: number;
  
  // Trailing
  use_trailing: boolean;
  trailing_start: number;
  trailing_dist: number;
  
  // Filtros Macro
  use_nfci: boolean;
  use_cs_alpha: boolean;
  cs_sma: number;
  cs_std_mult: number;
  cs_low_threshold: number;
}

export interface AlphaQuantSignal {
  timestamp: number;
  date: string;
  type: 'L-SQ' | 'REC' | 'TOP-REV' | 'BOT-REV';
  direction: 'long' | 'short';
  price: number;
  stopLoss: number;
  takeProfit: number;
  confidence: number;
  triggers: string[];
  macroFilters: {
    nfci?: 'pass' | 'blocked';
    csAlpha?: 'pass' | 'blocked';
  };
}

export interface AlphaQuantTrade {
  timestamp: number;
  date: string;
  type: 'entry' | 'exit';
  signalType: 'L-SQ' | 'REC' | 'TOP-REV' | 'BOT-REV';
  side: 'long' | 'short';
  price: number;
  pnl?: number;
  exitReason?: string;
}

export interface AlphaQuantResult {
  totalReturn: number;
  sharpeRatio: number;
  maxDrawdown: number;
  winRate: number;
  totalTrades: number;
  profitFactor: number;
  avgWin: number;
  avgLoss: number;
  finalCapital: number;
  equityCurve: { date: string; value: number }[];
  signalBreakdown: {
    lsq: { count: number; winRate: number };
    rec: { count: number; winRate: number };
    topRev: { count: number; winRate: number };
    botRev: { count: number; winRate: number };
  };
}

export const DEFAULT_ALPHA_QUANT_CONFIG: AlphaQuantConfig = {
  // Fontes
  symbol: 'BTCUSDT',
  
  // Thresholds 24H (dos prints do usuário)
  oi_up_lsq: 7,
  oi_down_rec: 2,
  px_lo: -5,
  px_hi: 5,
  fund_bp_lsq: 20,
  fund_bp_rec: 10,
  px_hold_rec: -1,
  
  // Reversões (valores do Pine Script)
  diff_pp: 5,
  top_px_up_24: 1,
  top_diff_neg_24: 1,        // Pine: top_diff_neg24 = 1.0 (aplicamos negativo)
  top_fund_lvl_bps: 5,
  top_fund_delta_bps: 5,
  bot_px_dn_24: -4,          // Pine: bot_px_dn24 = 4.0 (aplicamos negativo: preço caiu)
  bot_diff_pos_24: 2,        // Pine: bot_diff_pos24 = 2.0 (diff positivo)
  bot_fund_lvl_bps: -100,    // Pine: bot_fund_lvl_bps = -100
  bot_fund_delta_bps: -4,    // Pine: bot_fund_delta_bps = -4
  
  // Triggers TOP
  use_trg_diff_top: true,
  use_trg_fund_top: true,
  use_trg_oi_top: false,
  
  // Triggers BOT
  use_trg_diff_bot: true,
  use_trg_fund_bot: true,
  use_trg_oi_bot: true,
  bot_oi_flush_E: 1.0,
  
  // Sinais ativos
  enable_lsq: true,
  enable_rec: false,
  enable_top_rev: true,
  enable_bot_rev: true,
  
  // Stop/TP
  lsq_sl_pct: 10,
  lsq_tp_pct: 21,
  rec_sl_pct: 5,
  rec_tp_pct: 10,
  top_sl_pct: 8,
  top_tp_pct: 15,
  bot_sl_pct: 8,
  bot_tp_pct: 15,
  
  // Trailing
  use_trailing: true,
  trailing_start: 5,
  trailing_dist: 5,
  
  // Filtros Macro
  use_nfci: true,
  use_cs_alpha: true,
  cs_sma: 120,
  cs_std_mult: 2.2,
  cs_low_threshold: 1.5,
};

// =====================================================================
// ========================== HOOK PRINCIPAL ============================
// =====================================================================

export const useAlphaQuantSignals = () => {
  const [isLoading, setIsLoading] = useState(false);
  const [data, setData] = useState<EnrichedCandleData[]>([]);
  const [signals, setSignals] = useState<AlphaQuantSignal[]>([]);
  const [trades, setTrades] = useState<AlphaQuantTrade[]>([]);
  const [results, setResults] = useState<AlphaQuantResult | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [config, setConfig] = useState<AlphaQuantConfig>(DEFAULT_ALPHA_QUANT_CONFIG);
  
  // Fetch macro data (NFCI & CS Alpha)
  const fetchMacroData = useCallback(async () => {
    try {
      // Fetch CS Alpha data
      const { data: csData } = await supabase
        .from('market_metrics_cache')
        .select('data')
        .eq('id', 'credit_spread_alpha')
        .single();
      
      return {
        csAlpha: csData?.data || null,
      };
    } catch (err) {
      console.warn('Error fetching macro data:', err);
      return { csAlpha: null };
    }
  }, []);
  
  // Load 30 days of DAILY data from Binance (strategy runs on 1D timeframe)
  const loadData = useCallback(async (symbol = 'BTCUSDT') => {
    setIsLoading(true);
    setError(null);
    
    try {
      console.log(`[AlphaQuant] Loading 30 days of DAILY data for ${symbol}...`);
      const historicalData = await binanceService.fetchHistoricalData(symbol, '1d', '30d');
      setData(historicalData);
      console.log(`[AlphaQuant] Loaded ${historicalData.length} daily candles`);
      return historicalData;
    } catch (err) {
      const errorMessage = err instanceof Error ? err.message : 'Failed to load data';
      setError(errorMessage);
      throw new Error(errorMessage);
    } finally {
      setIsLoading(false);
    }
  }, []);
  
  // Detect signals based on config
  const detectSignals = useCallback((
    candles: EnrichedCandleData[],
    cfg: AlphaQuantConfig
  ): { signals: AlphaQuantSignal[]; signalArray: number[] } => {
    const detectedSignals: AlphaQuantSignal[] = [];
    const signalArray = new Array(candles.length).fill(0);
    
    // For daily candles, lookback of 1 day = 1 candle (not 24 hours)
    const lookback24h = 1;
    
    console.log(`[AlphaQuant] Detecting signals in ${candles.length} daily candles...`);
    
    for (let i = lookback24h; i < candles.length; i++) {
      const current = candles[i];
      
      // Get PX-OI data (now correctly calculated using 1H OI data)
      const priceChange24h = current.priceChange24h || 0;
      const oiChange24h = current.oiChange24h || 0;
      const diff24h = current.priceDiff24h || 0; // diff = priceChange - oiChange
      // funding24h is now already in bps from binanceService (calculated as fundingPerHour * 24 * 10000)
      const funding24h = (current.funding24h || 0) * 10000; // funding24h stored as decimal, convert to bps
      
      // Debug logging for January 10-15 candles
      const candleDate = current.date.slice(0, 10);
      if (candleDate >= '2026-01-10' && candleDate <= '2026-01-15') {
        console.log(`[AlphaQuant Debug] ${candleDate}:`, {
          price: current.close.toFixed(2),
          priceChange24h: priceChange24h.toFixed(2) + '%',
          oiChange24h: oiChange24h.toFixed(2) + '%',
          diff24h: diff24h.toFixed(2),
          funding24h_bps: funding24h.toFixed(2),
          openInterest: current.openInterest?.toLocaleString() || 'N/A'
        });
      }
      
      // fundingDelta is now correctly calculated in binanceService using 1H funding data
      // It represents: (fundingPerHour[now] - fundingPerHour[24h ago]) * 24 * 10000 bps
      const fundingDelta = (current.fundingDelta24h || 0) * 10000; // Convert to bps
      
      // OI delta for flush detection
      const oiDelta2h = i >= 2 
        ? ((current.openInterest - candles[i - 2].openInterest) / candles[i - 2].openInterest) * 100
        : 0;
      
      let signalType: AlphaQuantSignal['type'] | null = null;
      let direction: 'long' | 'short' | null = null;
      let triggers: string[] = [];
      let confidence = 0;
      
      // ================== L-SQ (Long Squeeze) - SHORT ==================
      if (cfg.enable_lsq) {
        const oiCondition = oiChange24h >= cfg.oi_up_lsq;
        const priceCondition = priceChange24h >= cfg.px_lo && priceChange24h <= cfg.px_hi;
        const fundingCondition = funding24h <= cfg.fund_bp_lsq;
        
        if (oiCondition && priceCondition && fundingCondition) {
          signalType = 'L-SQ';
          direction = 'short';
          triggers = ['OI↑', 'Price Band', 'Funding≤Threshold'];
          confidence = 0.8;
        }
      }
      
      // ================== REC (Recovery) - LONG ==================
      if (cfg.enable_rec && !signalType) {
        const oiCondition = oiChange24h <= cfg.oi_down_rec;
        const priceCondition = priceChange24h >= cfg.px_hold_rec;
        const fundingCondition = funding24h <= cfg.fund_bp_rec;
        
        if (oiCondition && priceCondition && fundingCondition) {
          signalType = 'REC';
          direction = 'long';
          triggers = ['OI↓', 'Price Hold', 'Funding≤Threshold'];
          confidence = 0.75;
        }
      }
      
      // ================== TOP-REV (Top Reversal) - SHORT ==================
      if (cfg.enable_top_rev && !signalType) {
        // Setup conditions
        const priceUp = priceChange24h >= cfg.top_px_up_24;
        const diffNeg = diff24h <= cfg.top_diff_neg_24;
        const fundingHigh = funding24h >= cfg.top_fund_lvl_bps;
        
        const setupMet = priceUp && diffNeg;
        
        if (setupMet) {
          // Check triggers
          let triggered = false;
          const triggerList: string[] = [];
          
          if (cfg.use_trg_diff_top) {
            // Diff crosses down through +diff_pp
            const prevDiff = candles[i - 1]?.priceDiff24h || 0;
            if (prevDiff > cfg.diff_pp && diff24h <= cfg.diff_pp) {
              triggered = true;
              triggerList.push('Diff↓');
            }
          }
          
          if (cfg.use_trg_fund_top && fundingDelta <= 0) {
            triggered = true;
            triggerList.push('ΔFund≤0');
          }
          
          if (cfg.use_trg_oi_top && oiDelta2h <= 0) {
            triggered = true;
            triggerList.push('ΔOI≤0');
          }
          
          if (triggered) {
            signalType = 'TOP-REV';
            direction = 'short';
            triggers = ['Setup', ...triggerList];
            confidence = fundingHigh ? 0.85 : 0.7;
          }
        }
      }
      
      // ================== BOT-REV (Bottom Reversal) - LONG ==================
      // Pine Script: bot24 = (px24 <= -bot_px_dn24) and (dpxoi24 >= bot_diff_pos24) 
      //              and (fund_lvl_24h_bps <= bot_fund_lvl_bps) and (df_bp24 <= bot_fund_delta_bps)
      if (cfg.enable_bot_rev && !signalType) {
        // Setup conditions - TODAS as 4 condições do Pine Script
        const priceDown = priceChange24h <= cfg.bot_px_dn_24;  // px24 <= -4 (cfg já negativo)
        const diffPos = diff24h >= cfg.bot_diff_pos_24;         // dpxoi24 >= +2
        const fundingLow = funding24h <= cfg.bot_fund_lvl_bps;  // funding <= -100 bps
        const fundingDeltaLow = fundingDelta <= cfg.bot_fund_delta_bps; // ΔFunding <= -4 bps
        
        // Pine Script exige TODAS as 4 condições para setup
        const setupMet = priceDown && diffPos && fundingLow && fundingDeltaLow;
        
        // Debug logging para janeiro de 2026
        const candleDateDbg = current.date.slice(0, 10);
        if (candleDateDbg >= '2026-01-10' && candleDateDbg <= '2026-01-20') {
          console.log(`[BOT-REV Setup] ${candleDateDbg}:`, {
            conditions: {
              priceDown: `${priceChange24h.toFixed(2)}% <= ${cfg.bot_px_dn_24}% → ${priceDown}`,
              diffPos: `${diff24h.toFixed(2)} >= ${cfg.bot_diff_pos_24} → ${diffPos}`,
              fundingLow: `${funding24h.toFixed(2)} <= ${cfg.bot_fund_lvl_bps} → ${fundingLow}`,
              fundingDeltaLow: `${fundingDelta.toFixed(2)} <= ${cfg.bot_fund_delta_bps} → ${fundingDeltaLow}`,
            },
            setupMet,
            values: { priceChange24h, diff24h, funding24h, fundingDelta, oiChange24h }
          });
        }
        
        if (setupMet) {
          // Check triggers
          let triggered = false;
          const triggerList: string[] = [];
          
          if (cfg.use_trg_diff_bot) {
            // Diff crosses up through -diff_pp
            const prevDiff = candles[i - 1]?.priceDiff24h || 0;
            if (prevDiff < -cfg.diff_pp && diff24h >= -cfg.diff_pp) {
              triggered = true;
              triggerList.push('Diff↑');
            }
          }
          
          if (cfg.use_trg_fund_bot && fundingDelta >= 0) {
            triggered = true;
            triggerList.push('ΔFund≥0');
          }
          
          if (cfg.use_trg_oi_bot && oiDelta2h <= -cfg.bot_oi_flush_E) {
            triggered = true;
            triggerList.push('OI Flush');
          }
          
          if (triggered) {
            signalType = 'BOT-REV';
            direction = 'long';
            triggers = ['Setup', ...triggerList];
            confidence = fundingLow ? 0.85 : 0.7;
            
            console.log(`[BOT-REV TRIGGERED] ${current.date}:`, {
              price: current.close,
              triggers: triggerList
            });
          }
        }
      }
      
      // Create signal if detected
      if (signalType && direction) {
        // Get SL/TP based on signal type
        let slPct = 0, tpPct = 0;
        switch (signalType) {
          case 'L-SQ':
            slPct = cfg.lsq_sl_pct;
            tpPct = cfg.lsq_tp_pct;
            break;
          case 'REC':
            slPct = cfg.rec_sl_pct;
            tpPct = cfg.rec_tp_pct;
            break;
          case 'TOP-REV':
            slPct = cfg.top_sl_pct;
            tpPct = cfg.top_tp_pct;
            break;
          case 'BOT-REV':
            slPct = cfg.bot_sl_pct;
            tpPct = cfg.bot_tp_pct;
            break;
        }
        
        const stopLoss = direction === 'long'
          ? current.close * (1 - slPct / 100)
          : current.close * (1 + slPct / 100);
          
        const takeProfit = direction === 'long'
          ? current.close * (1 + tpPct / 100)
          : current.close * (1 - tpPct / 100);
        
        const signal: AlphaQuantSignal = {
          timestamp: current.timestamp,
          date: current.date,
          type: signalType,
          direction,
          price: current.close,
          stopLoss,
          takeProfit,
          confidence,
          triggers,
          macroFilters: {
            nfci: cfg.use_nfci ? 'pass' : undefined,
            csAlpha: cfg.use_cs_alpha ? 'pass' : undefined,
          },
        };
        
        detectedSignals.push(signal);
        signalArray[i] = direction === 'long' ? 1 : -1;
      }
    }
    
    return { signals: detectedSignals, signalArray };
  }, []);
  
  // Simulate backtest
  const simulateBacktest = useCallback((
    candles: EnrichedCandleData[],
    detectedSignals: AlphaQuantSignal[],
    signalArray: number[],
    initialCapital: number,
    cfg: AlphaQuantConfig
  ): { result: AlphaQuantResult; trades: AlphaQuantTrade[] } => {
    const executedTrades: AlphaQuantTrade[] = [];
    const equityCurve: { date: string; value: number }[] = [];
    
    let capital = initialCapital;
    let position: { side: 'long' | 'short'; entry: number; signal: AlphaQuantSignal; trailingStop?: number } | null = null;
    let wins = 0;
    let losses = 0;
    let totalWinAmount = 0;
    let totalLossAmount = 0;
    let maxCapital = initialCapital;
    let maxDrawdown = 0;
    
    // Signal breakdown
    const breakdown = {
      lsq: { count: 0, wins: 0 },
      rec: { count: 0, wins: 0 },
      topRev: { count: 0, wins: 0 },
      botRev: { count: 0, wins: 0 },
    };
    
    for (let i = 0; i < candles.length; i++) {
      const candle = candles[i];
      const signalValue = signalArray[i];
      const currentDate = new Date(candle.timestamp).toISOString().split('T')[0];
      
      // Check for exit if in position
      if (position) {
        let exitReason: string | null = null;
        
        // Check stop loss
        if (position.side === 'long') {
          if (candle.low <= position.signal.stopLoss) {
            exitReason = 'Stop Loss';
          } else if (candle.high >= position.signal.takeProfit) {
            exitReason = 'Take Profit';
          }
          
          // Trailing stop
          if (cfg.use_trailing && position.trailingStop) {
            if (candle.low <= position.trailingStop) {
              exitReason = 'Trailing Stop';
            } else if (candle.close > position.entry * (1 + cfg.trailing_start / 100)) {
              // Update trailing stop
              const newTrail = candle.close * (1 - cfg.trailing_dist / 100);
              position.trailingStop = Math.max(position.trailingStop, newTrail);
            }
          }
        } else { // short
          if (candle.high >= position.signal.stopLoss) {
            exitReason = 'Stop Loss';
          } else if (candle.low <= position.signal.takeProfit) {
            exitReason = 'Take Profit';
          }
          
          // Trailing stop for shorts
          if (cfg.use_trailing && position.trailingStop) {
            if (candle.high >= position.trailingStop) {
              exitReason = 'Trailing Stop';
            } else if (candle.close < position.entry * (1 - cfg.trailing_start / 100)) {
              const newTrail = candle.close * (1 + cfg.trailing_dist / 100);
              position.trailingStop = Math.min(position.trailingStop, newTrail);
            }
          }
        }
        
        // Close position if exit reason
        if (exitReason) {
          const exitPrice = exitReason === 'Stop Loss' 
            ? position.signal.stopLoss 
            : exitReason === 'Take Profit'
              ? position.signal.takeProfit
              : candle.close;
          
          const pnl = position.side === 'long'
            ? ((exitPrice - position.entry) / position.entry) * 100
            : ((position.entry - exitPrice) / position.entry) * 100;
          
          const tradeReturn = pnl / 100;
          
          if (pnl > 0) {
            wins++;
            totalWinAmount += capital * tradeReturn;
            
            // Update breakdown
            const key = position.signal.type === 'L-SQ' ? 'lsq' 
              : position.signal.type === 'REC' ? 'rec'
              : position.signal.type === 'TOP-REV' ? 'topRev' : 'botRev';
            breakdown[key].wins++;
          } else {
            losses++;
            totalLossAmount += Math.abs(capital * tradeReturn);
          }
          
          capital = capital * (1 + tradeReturn);
          
          executedTrades.push({
            timestamp: candle.timestamp,
            date: currentDate,
            type: 'exit',
            signalType: position.signal.type,
            side: position.side,
            price: exitPrice,
            pnl,
            exitReason,
          });
          
          position = null;
        }
      }
      
      // Check for entry if not in position
      if (!position && signalValue !== 0) {
        const signal = detectedSignals.find(s => s.timestamp === candle.timestamp);
        if (signal) {
          position = {
            side: signal.direction,
            entry: signal.price,
            signal,
            trailingStop: cfg.use_trailing
              ? (signal.direction === 'long' 
                  ? signal.price * (1 - cfg.trailing_dist / 100)
                  : signal.price * (1 + cfg.trailing_dist / 100))
              : undefined,
          };
          
          // Update breakdown
          const key = signal.type === 'L-SQ' ? 'lsq' 
            : signal.type === 'REC' ? 'rec'
            : signal.type === 'TOP-REV' ? 'topRev' : 'botRev';
          breakdown[key].count++;
          
          executedTrades.push({
            timestamp: candle.timestamp,
            date: currentDate,
            type: 'entry',
            signalType: signal.type,
            side: signal.direction,
            price: signal.price,
          });
        }
      }
      
      // Calculate current value
      let currentValue = capital;
      if (position) {
        const unrealizedPnl = position.side === 'long'
          ? ((candle.close - position.entry) / position.entry)
          : ((position.entry - candle.close) / position.entry);
        currentValue = capital * (1 + unrealizedPnl);
      }
      
      // Track drawdown
      if (currentValue > maxCapital) {
        maxCapital = currentValue;
      }
      const drawdown = ((maxCapital - currentValue) / maxCapital) * 100;
      if (drawdown > maxDrawdown) {
        maxDrawdown = drawdown;
      }
      
      equityCurve.push({
        date: currentDate,
        value: currentValue,
      });
    }
    
    // Close any remaining position at end
    if (position) {
      const lastCandle = candles[candles.length - 1];
      const pnl = position.side === 'long'
        ? ((lastCandle.close - position.entry) / position.entry) * 100
        : ((position.entry - lastCandle.close) / position.entry) * 100;
      
      capital = capital * (1 + pnl / 100);
      
      executedTrades.push({
        timestamp: lastCandle.timestamp,
        date: new Date(lastCandle.timestamp).toISOString().split('T')[0],
        type: 'exit',
        signalType: position.signal.type,
        side: position.side,
        price: lastCandle.close,
        pnl,
        exitReason: 'End of Period',
      });
      
      if (pnl > 0) wins++;
      else losses++;
    }
    
    // Calculate metrics
    const totalReturn = ((capital - initialCapital) / initialCapital) * 100;
    const totalTrades = wins + losses;
    const winRate = totalTrades > 0 ? (wins / totalTrades) * 100 : 0;
    const avgWin = wins > 0 ? (totalWinAmount / wins / initialCapital) * 100 : 0;
    const avgLoss = losses > 0 ? (totalLossAmount / losses / initialCapital) * 100 : 0;
    const profitFactor = avgLoss > 0 ? avgWin / avgLoss : avgWin > 0 ? Infinity : 0;
    
    // Sharpe ratio
    const returns = equityCurve.slice(1).map((point, i) =>
      (point.value - equityCurve[i].value) / equityCurve[i].value
    );
    const avgReturn = returns.reduce((sum, ret) => sum + ret, 0) / returns.length || 0;
    const stdDev = Math.sqrt(
      returns.reduce((sum, ret) => sum + Math.pow(ret - avgReturn, 2), 0) / returns.length
    ) || 1;
    const sharpeRatio = (avgReturn / stdDev) * Math.sqrt(252 * 24); // Annualized for hourly
    
    const result: AlphaQuantResult = {
      totalReturn,
      sharpeRatio: isFinite(sharpeRatio) ? sharpeRatio : 0,
      maxDrawdown,
      winRate,
      totalTrades,
      profitFactor: isFinite(profitFactor) ? profitFactor : 0,
      avgWin,
      avgLoss,
      finalCapital: capital,
      equityCurve,
      signalBreakdown: {
        lsq: { count: breakdown.lsq.count, winRate: breakdown.lsq.count > 0 ? (breakdown.lsq.wins / breakdown.lsq.count) * 100 : 0 },
        rec: { count: breakdown.rec.count, winRate: breakdown.rec.count > 0 ? (breakdown.rec.wins / breakdown.rec.count) * 100 : 0 },
        topRev: { count: breakdown.topRev.count, winRate: breakdown.topRev.count > 0 ? (breakdown.topRev.wins / breakdown.topRev.count) * 100 : 0 },
        botRev: { count: breakdown.botRev.count, winRate: breakdown.botRev.count > 0 ? (breakdown.botRev.wins / breakdown.botRev.count) * 100 : 0 },
      },
    };
    
    return { result, trades: executedTrades };
  }, []);
  
  // Run full backtest
  const runBacktest = useCallback(async (
    customConfig?: Partial<AlphaQuantConfig>,
    initialCapital = 10000
  ) => {
    setIsLoading(true);
    setError(null);
    
    try {
      const cfg = { ...config, ...customConfig };
      setConfig(cfg);
      
      // Load data if needed
      let candles = data;
      if (candles.length === 0) {
        candles = await loadData(cfg.symbol);
      }
      
      console.log(`[AlphaQuant] Running backtest with ${candles.length} candles...`);
      
      // Detect signals
      const { signals: detectedSignals, signalArray } = detectSignals(candles, cfg);
      setSignals(detectedSignals);
      
      console.log(`[AlphaQuant] Detected ${detectedSignals.length} signals`);
      
      // Run simulation
      const { result, trades: executedTrades } = simulateBacktest(
        candles,
        detectedSignals,
        signalArray,
        initialCapital,
        cfg
      );
      
      setResults(result);
      setTrades(executedTrades);
      setData(candles); // Ensure data state is updated
      
      console.log(`[AlphaQuant] Backtest complete: ${result.totalTrades} trades, ${result.totalReturn.toFixed(2)}% return`);
      
      return { result, signals: detectedSignals, trades: executedTrades, candles };
    } catch (err) {
      const errorMessage = err instanceof Error ? err.message : 'Backtest failed';
      setError(errorMessage);
      throw new Error(errorMessage);
    } finally {
      setIsLoading(false);
    }
  }, [config, data, loadData, detectSignals, simulateBacktest]);
  
  return {
    isLoading,
    data,
    signals,
    trades,
    results,
    error,
    config,
    setConfig,
    loadData,
    runBacktest,
    fetchMacroData,
  };
};
